﻿import React, { useState, useEffect } from 'react';
import HazardStatementsComponent from './Safety/HazardStatementsComponent';
import { ItemBasicInformation } from '../models/item-basic-information';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { DocumentType, ModalMessageType, NoteType, HazardPictogramTypes } from '../models/enums';
import { Hazard } from '../models/Common/hazard';
import { ProductHazard } from '../models/product-hazard';
import { ToastProps } from '../models/toast-props';

import DataGrid, {
    Column,
    Selection,
    Button as ColumnButton,
    Toolbar,
    Editing,
    Item as ToolbarItem,
    Paging,
    SearchPanel,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Button } from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import Toast from 'devextreme-react/toast';
import DocumentLoader from './DocumentLoader';
import NoteViewer from './NoteViewer';

let key = 1;

export interface Props {
    productId?: string;
    canEdit: boolean;
}

export default function ProductSafetyComponent(props: Props) {
    const [productHazards, setProductHazards] =
        useState<Hazard[]>();

    const [selectedProductHazard, setSelectedProductHazard] =
        useState<Hazard>();

    const [allHazardIcons, setAllHazardIcons] =
        useState<number[]>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>(false);

    const [selectHazardsDialogVisible, setSelectHazardsDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>(false);

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.productId]);

    key++;

    return (
        <div className="pw-form-section">
            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Safety</div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-10">
                    <CheckBox id="sds" className="pw-check-box"
                        text="SDS"
                        onValueChanged={safetyFieldChanged}
                        value={true} />
                </div>

                <div className="pw-form-unit pw-comp-10">
                    <CheckBox id="labels" className="pw-check-box"
                        text="Labels"
                        onValueChanged={safetyFieldChanged}
                        value={false} />
                </div>

                <div className="pw-form-unit pw-comp-10">
                    <CheckBox id="printOuts" className="pw-check-box"
                        text="Printouts"
                        onValueChanged={safetyFieldChanged}
                        value={false} />
                </div>
            </div>

            <NoteViewer height='6rem' title="Additional EHS Handling Information"
                noteType={NoteType.ProductEhsHandlingInformation} parentId={props.productId} />

            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-1 pw-margin-bottom-05">All Hazard Icons</div>

            {allHazardIcons && allHazardIcons.map((item, i) =>
                <div key={i.toString()}>
                    <div className="hazard-card">
                        <div className={getIconClassByHazardPictogramId(item)} />
                    </div>
                </div>)}

            <div className="pw-comp-row pw-margin-top-1">

                <DataGrid
                    height="16rem" width="100%"
                    dataSource={productHazards ? productHazards : []}
                    wordWrapEnabled={true}
                    allowColumnResizing={true}
                    columnResizingMode='widget'
                    keyExpr="hazardId"
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    onSelectionChanged={onProductHazardsSelectionChanged}
                    onRowRemoving={onHazardRemoving}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />
                    <Selection mode='single' />
                    <SearchPanel visible={true} />

                    <Column type="buttons" width="4rem">
                        <ColumnButton icon="link" />
                        <ColumnButton name="delete" />
                    </Column>

                    <Column dataField='hCode' caption="H-Code" dataType='string' width='5rem' />
                    <Column dataField='statement' cellRender={renderHazardStatement} dataType='string' />
                    <Column dataField='hazardClass' dataType='string' width='10rem' />
                    <Column dataField='category' dataType='string' width="10rem" />
                    <Column dataField='unModelRegulationsClass' dataType='string' width="8rem" />
                    <Column dataField='signalWord' cellRender={renderSignalWord} dataType='string' width="8rem" />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Hazard Statements</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location='after' name="searchPanel" />

                        <ToolbarItem location='after'>
                            <Button type="normal" icon="plus" hint="add hazards to product"
                                disabled={!props.canEdit}
                                onClick={onAddHazardsToProductClick} />
                        </ToolbarItem>
                    </Toolbar>

                    <Editing mode="popup"
                        useIcons={true}
                        allowDeleting={true} />
                </DataGrid>

            </div>

            <div>
                <div className="lq-header pw-margin-top-05">
                    <div className="lq-header-title">Safety Data Sheets</div>
                </div>

                <DocumentLoader parentId={props.productId}
                    documentType={DocumentType.ProductSds}
                    canDelete={props.canEdit} />
            </div>

            {/* LEAVE FOR NOW:
            <div className="aurum-note">These icons shown here just for demo. These can be used anywhere in the DMA application, as well as printed on labels, documents</div>

            <div className="pw-comp-row">
                <div className="hazard-card">
                    <div className="hazard-corrosive" />
                </div>
                <div className="hazard-card">
                    <div className="hazard-environmental-hazard" />
                </div>
                <div className="hazard-card">
                    <div className="hazard-compressed-gas" />
                </div>
                <div className="hazard-card">
                    <div className="hazard-harmful" />
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="hazard-card">
                    <div className="hazard-explosive" />
                </div>
                <div className="hazard-card">
                    <div className="hazard-flammable" />
                </div>
                <div className="hazard-card">
                    <div className="hazard-oxidizing" />
                </div>
                <div className="hazard-card">
                    <div className="hazard-health-hazard" />
                </div>
                <div className="hazard-card">
                    <div className="hazard-toxic" />
                </div>
            </div>
            */}
            {selectHazardsDialogVisible &&
                <DialogPopup visible={selectHazardsDialogVisible}
                    onHiding={hideSelectHazardsDialog}
                    title="Select Hazards"
                    fullScreen={true} >
                    <HazardStatementsComponent modal={true}
                        onSubmit={onSubmitSelectedHazards} onClose={hideSelectHazardsDialog}
                        key={`hsc${key}`} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    )

    function getIconClassByHazardPictogramId(pictogramId: number) {
        switch (pictogramId) {
            case HazardPictogramTypes.Explosive:
                return "hazard-explosive";
            case HazardPictogramTypes.Flammable:
                return "hazard-flammable";
            case HazardPictogramTypes.Oxidizing:
                return "hazard-oxidizing";
            case HazardPictogramTypes.CompressedGas:
                return "hazard-compressed-gas";
            case HazardPictogramTypes.Corrosive:
                return "hazard-corrosive";
            case HazardPictogramTypes.Toxic:
                return "hazard-toxic";
            case HazardPictogramTypes.Harmful:
                return "hazard-harmful";
            case HazardPictogramTypes.HealthHazard:
                return "hazard-health-hazard";
            case HazardPictogramTypes.EnvironmentalHazard:
                return "hazard-environmental-hazard";
            default:
                return "";
        }
    }

    function onAddHazardsToProductClick() {
        showSelectHazardsDialog();
    }

    function safetyFieldChanged(e: any) {



    }

    function onProductHazardsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedProductHazard(e.selectedRowsData[0]);
    }

    async function onSubmitSelectedHazards(selectedHazards: Hazard[]) {
        console.log("onSubmitSelectedHazards");
        console.log(selectedHazards);

        if (selectedHazards) {
            let newProductHazards: ProductHazard[] = [];

            selectedHazards.forEach(h => {
                //find any already added
                if (!productHazards.find(ph => ph.hazardId === h.hazardId))

                    newProductHazards.push({
                        productId: props.productId,
                        hazardId: h.hazardId
                    });
            })

            if (newProductHazards) {
                //console.log("newHazards to be added...");
                //console.log(newProductHazards);

                await addHazardsToProduct(newProductHazards)
                    .then(() => {
                        showToast("Hazards Added Successfully", "success");
                        getProductHazards();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Adding Hazard");
                    });
            }
        }

        hideSelectHazardsDialog();
    }

    async function onHazardRemoving(e: any) {
        if (e.data && e.data.hazardId) {
            await removeHazardFromProduct(e.data.hazardId)
                .then(() => {
                    showToast("Hazard Removed Successfully", "success");
                    getProductHazards();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Removing Hazard");
                });
        }
    }

    async function getProductHazards() {
        await CrudFunctions.getData(`products/hazards/${props.productId}`)
            .then((data) => {
                console.log("Hazard Data..");
                console.log(data);

                let hazards = data as Hazard[];
                let allHazardPictograms: number[] = [];

                hazards.forEach(h => {
                    allHazardPictograms.push(...h.pictograms);
                });

                let distinctPictograms = new Set(allHazardPictograms);

                setAllHazardIcons([...distinctPictograms]);

                setProductHazards(hazards);

                //console.log("[...distinctPictograms] ------");
                //console.log([...distinctPictograms]);

            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Product Hazards");
            });
    }

    async function addHazardsToProduct(addedHazards: ProductHazard[]) {
        return await CrudFunctions.postData("products/hazards", addedHazards);
    }

    async function removeHazardFromProduct(hazardId: string) {
        if (props.productId && hazardId)
            return await CrudFunctions.deleteData(`products/hazard/${props.productId}/${hazardId}`);
    }

    async function getAllData() {
        await Promise.all([
            getProductHazards()
        ])
    }

    function renderHazardStatement(e: any) {
        if (e.data) {
            if (e.data.obsolete) {
                return <div><span className="pw-red">{"(Obsolete) "}</span><span>{e.data.statement}</span></div>
            }
            else {
                return <div>{e.data.statement}</div>
            }
        }
        else
            return <div></div>;
    }

    function renderSignalWord(e: any) {
        if (e.data) {
            let css = '';
            if (e.data.signalWord === "Danger")
                css = "pw-red";
            else if (e.data.signalWord === "Warning")
                css = "pw-blue";

            return <div className={css}>{e.data.signalWord}</div>
        }
        else
            return <div></div>;
    }


    function showSelectHazardsDialog() {
        setSelectHazardsDialogVisible(true);
    }

    function hideSelectHazardsDialog() {
        setSelectHazardsDialogVisible(false);
    }


    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}